// React
import { useState } from "react";
import PropTypes from "prop-types";
import clsx from "clsx";
import "./DateTimePicker.css";

// MUI
import TextField from "@mui/material/TextField";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { DesktopDateTimePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { Paper } from "@mui/material";

// Icons
import DateRangeRoundedIcon from "@mui/icons-material/DateRangeRounded";

const theme = createTheme({
	palette: {
		primary: {
			main: "#019bd9",
		},
	},
});

export default function DateTimePicker(props) {
	const [open, setOpen] = useState(false);

	const handleShouldDisableDate = (date) => {
		if (props.onShouldDisableDate && date) {
			return props.onShouldDisableDate(date);
		}
	};

	const handleChange = (date) => {
		if (props.onActionUpdate && date) {
			props.onActionUpdate(date);
		}
	};

	const handleOnClose = () => {
		setOpen(false);
		if (props.onClose) {
			props.onClose();
		}
	};

	const handleAccept = () => {
		if (props.onAccept) {
			props.onAccept(props.defaultValue);
		}
		setOpen(false);
	};

	return (
		<ThemeProvider theme={theme}>
			<Paper
				elevation={0}
				className={"date-time-picker-container"}
				style={{ position: "relative", backgroundColor: "transparent", width: "100%" }}
				sx={props.sx}
			>
				<LocalizationProvider dateAdapter={AdapterMoment}>
					<DesktopDateTimePicker
						value={props.defaultValue}
						open={open}
						mask={props.mask}
						className={clsx({
							"text-input-error-date-picker": props.error,
						})}
						views={props.views}
						inputFormat={props.inputFormat}
						toolbarFormat={props.toolbarFormat}
						clearable={props.clearable}
						allowSameDateSelection={props.allowSameDateSelection}
						disabled={props.disabled}
						readOnly={props.readOnly}
						disableHighlightToday={props.disableHighlightToday}
						hideTabs={props.hideTabs}
						minDateTime={props.minDateTime}
						maxDateTime={props.maxDateTime}
						minutesStep={props.minutesStep}
						showToolbar={props.showToolbar}
						disableOpenPicker={props.disableOpenPicker}
						orientation={props.orientation}
						toolbarTitle={props.toolbarTitle}
						cancelText={props.cancelText}
						okText={props.okText}
						todayText={props.todayText}
						disableCloseOnSelect={props.disableCloseOnSelect}
						renderInput={(params) => (
							<TextField
								fullWidth
								disabled={props.disabled}
								onClick={() => {
									if (!props.disabled) {
										setOpen(true);
									}
									if (props?.onOpen) {
										props.onOpen(props.defaultValue);
									}
								}}
								className={clsx({
									"date-time-picker-input correct-shink-label": true,
								})}
								{...params}
								autoComplete={!props?.autocomplete ? "off" : props.autocomplete}
							/>
						)}
						components={{ OpenPickerIcon: DateRangeRoundedIcon }}
						PopperProps={{
							className: "custom-date-time-picker",
							style: {
								width: "100%",
								maxWidth: props.maxWidth,
							},
							placement: props.placement,
							disablePortal: true,
						}}
						onChange={handleChange}
						onClose={handleOnClose}
						onOpen={() => setOpen(true)}
						onAccept={handleAccept}
						shouldDisableDate={handleShouldDisableDate}
					/>
				</LocalizationProvider>
			</Paper>
		</ThemeProvider>
	);
}

DateTimePicker.propTypes = {
	views: PropTypes.array,
	defaultValue: PropTypes.object,
	mask: PropTypes.string,
	error: PropTypes.bool,
	inputFormat: PropTypes.string,
	toolbarFormat: PropTypes.string,
	clearable: PropTypes.bool,
	allowSameDateSelection: PropTypes.bool,
	disabled: PropTypes.bool,
	readOnly: PropTypes.bool,
	disableHighlightToday: PropTypes.bool,
	hideTabs: PropTypes.bool,
	minDateTime: PropTypes.object,
	maxDateTime: PropTypes.object,
	minutesStep: PropTypes.number,
	showToolbar: PropTypes.bool,
	disableOpenPicker: PropTypes.bool,
	orientation: PropTypes.string,
	toolbarTitle: PropTypes.string,
	cancelText: PropTypes.string,
	okText: PropTypes.string,
	todayText: PropTypes.string,
	disableCloseOnSelect: PropTypes.bool,
	onActionUpdate: PropTypes.func,
	onClose: PropTypes.func,
	onAccept: PropTypes.func,
	onOpen: PropTypes.func,
	autocomplete: PropTypes.string,
	sx: PropTypes.object,
	maxWidth: PropTypes.string,
	placement: PropTypes.string,
	onShouldDisableDate: PropTypes.func,
};
